import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { DollarSign, TrendingUp } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Quanto Ganha um Revendedor IPTV Pós Pago", href: "/blog/quanto-ganha-revendedor-iptv-pos-pago" }
  ];

  const sections = [
    {
      title: "Matemática do Lucro na Revenda",
      type: "featured" as const,
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-muted-foreground mb-6">
            O lucro na revenda de IPTV vem da diferença entre o seu custo por linha no painel e o valor de venda ao cliente final. No modelo pós-pago da P2 PRO, sua margem é potencializada porque você não tem o custo financeiro do capital parado.
          </p>
          <div className="p-8 rounded-3xl bg-electric/5 border border-electric/10 my-8">
            <h4 className="text-xl font-bold mb-4 text-center">Projeção Mensal de Faturamento</h4>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-black text-white">R$ 3.000</div>
                <div className="text-sm text-muted-foreground">Com 100 Clientes</div>
              </div>
              <div>
                <div className="text-3xl font-black text-electric">R$ 9.000</div>
                <div className="text-sm text-muted-foreground">Com 300 Clientes</div>
              </div>
              <div>
                <div className="text-3xl font-black text-white">R$ 15.000+</div>
                <div className="text-sm text-muted-foreground">Com 500+ Clientes</div>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground italic text-center">
            * Valores baseados em uma média de mercado. O seu lucro real dependerá do seu preço de venda e volume de clientes ativos.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Quanto Ganha um Revendedor IPTV Pós Pago"
      title="Quanto Ganha um Revendedor IPTV Pós Pago: Simulação de Lucro"
      subtitle="Veja como é possível construir uma renda de 5 dígitos mensais operando com o sistema pós-pago da P2 PRO."
      heroImage="https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitas pessoas entram no mercado de IPTV buscando uma renda extra, mas acabam descobrindo um negócio capaz de faturar mais do que muitos cargos executivos.",
        "Neste artigo, mostramos a realidade financeira de um revendedor que opera com o modelo pós-pago e como a escala de clientes impacta diretamente no seu lucro líquido no final do mês."
      ]}
      benefits={[
        { title: "Margens de 300%", description: "Otimize seus ganhos com um dos modelos de negócio mais lucrativos da internet." },
        { title: "Escala Ilimitada", description: "Seu lucro cresce proporcionalmente ao seu esforço de vendas." }
      ]}
      howItWorks={[
        { title: "Simulação", description: "Utilize nossos parâmetros para calcular sua meta de faturamento." },
        { title: "Execução", description: "Aplique nossas estratégias de vendas para alcançar seus objetivos." }
      ]}
      diferenciais={[
        { title: "Zero Estoque", description: "Todo o seu faturamento inicial pode ser lucro ou reinvestimento." }
      ]}
      faq={[
        { question: "Em quanto tempo começo a lucrar?", answer: "No sistema pós-pago, você já lucra na sua primeira venda, pois não precisou desembolsar capital para comprar créditos antecipadamente." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Quer Alcançar o Faturamento de 5 Dígitos?"
      ctaDescription="Venha para a P2 PRO e aprenda com quem já domina o mercado."
      ctaButtonText="Começar a Lucrar Agora"
    />
  );
};

export const Route = createFileRoute("/blog/quanto-ganha-revendedor-iptv-pos-pago")({
  head: () => ({
    meta: [
      { title: "Quanto Ganha um Revendedor IPTV Pós Pago - Simulação de Lucro | P2 PRO" },
      { name: "description", content: "Veja quanto você pode ganhar como revendedor IPTV operando no sistema pós-pago. Simulação de lucros, margens e estratégias de escala." }
    ],
  }),
  component: ArticlePage,
});
